import { useLoaderData, useRouteLoaderData, useParams, useNavigate } from 'react-router-dom'
import { useMemo } from 'react'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EmailIcon from '@mui/icons-material/Email'
import { useTranslation } from 'react-i18next'
import { GridColDef, GridInitialState } from '@mui/x-data-grid-premium'

import { ContributorRole, LoaderData, Participants } from '../../../types'
import {
  useParticipantOptionStatusOptions,
  useRolesTypeOptions,
  useSubEventsTypeOptions,
} from '../../../utils'
import type { Overview } from '../../../services/events'
import StripedDataGrid from '../../root/components/StripedDataGrid'
import { HEADER_HEIGHT } from '../../root/components/Header'
import { SUB_HEADER_HEIGHT } from '../../root/components/SubHeader'
import ParticipantOptionStatusTag from '../../events/components/ParticipantOptionStatusTag'
import { adminEventDetailsLoader, adminHotelOverviewLoader } from '../Admin.loader'
import { useContextShowStatistics } from '../Admin.Context'
import AdminEventDetails from './components/AdminEventDetails'
import ViewButtonGroup from './components/ViewButtonGroup'
import { Button } from '@mui/material'

const initialState: GridInitialState = {
  columns: {
    columnVisibilityModel: {
      id: false,
    },
  },
}

export default function AdminContributors() {
  const { t } = useTranslation('admin')
  const { eventId } = useParams()
  const navigate = useNavigate()
  if (!eventId || Number.isNaN(eventId)) throw new Error('Event ID is required')

  const showStatistics = useContextShowStatistics()

  const data = useLoaderData() as LoaderData<ReturnType<typeof adminHotelOverviewLoader>>
  const { event } = useRouteLoaderData('adminEventDetails') as LoaderData<
    ReturnType<typeof adminEventDetailsLoader>
  >
  const rolesOptions = useRolesTypeOptions(t)
  const participantOptionStatusOptions = useParticipantOptionStatusOptions(t)

  const columns = useMemo(() => {
    const arr = [
      {
        field: 'actions',
        headerName: t('Actions'),
        type: 'actions',
        width: 100,
        filterable: false,
        disableExport: true,
        renderCell: params => {
          const row = params.row as Overview['rows'][number]
          return (
            <Stack direction="row">
              {/* <Tooltip title={t('Voir détails du participant')}>
                <IconButton
                  color="primary"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    navigate(`/admin/details/${eventId}/participants/${row.id as string}`)
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip> */}
              <Tooltip title={t('Envoyer un email')}>
                <IconButton
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    const subject = `SFCTCV Événement ${event?.name} - Information sur votre repas`
                    const body = `Bonjour ${row.first_name} ${row.last_name},%0D%0A%0D%0ANous venons de mettre à jour votre repas pour l'événement ${event?.name}.%0D%0A%0D%0ACordialement,%0D%0AL'équipe SFCTCV`

                    window.open(
                      `mailto:${row.e_mail as string}?subject=${subject}&body=${body}`,
                      '_blank'
                    )
                  }}
                >
                  <EmailIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )
        },
      },
      ...(data?.columns ?? []),
    ] as Overview['columns']
    return arr.map(col => {
      switch (col.dbField) {
        case 'participant_id':
          return {
            ...col,
            field: 'id',
            headerName: t('ID'),
            width: 60,
          }
        case 'first_name':
          return {
            ...col,
            headerName: t('Prénom'),
            width: 150,
          }
        case 'last_name':
          return {
            ...col,
            headerName: t('Nom'),
            width: 150,
          }
        case 'email':
          return {
            ...col,
            headerName: t('Email'),
            width: 200,
          }
        case 'nom':
          return {
            ...col,
            headerName: t('Nom'),
            width: 300,
          }
        default:
          break
      }
      if (col.dbField?.includes('status')) {
        return {
          ...col,
          headerName: t('Statut Participant'),
          width: 210,
          display: 'flex',
          type: 'singleSelect',
          valueOptions: participantOptionStatusOptions,
          renderCell: params => (
            <ParticipantOptionStatusTag
              size="small"
              status={params.value as Participants['participant_options'][number]['status']}
            />
          ),
        }
      }
      if (col.dbField?.includes('roles')) {
        return {
          ...col,
          headerName: t('Rôles'),
          width: 150,
          type: 'singleSelect',
          valueOptions: rolesOptions,
          valueFormatter: (value: ContributorRole[]) =>
            value
              ? value
                  .map(el => rolesOptions.find(option => option.value === el)?.label ?? 'NC')
                  .join(', ')
              : 'N/A',
        }
      }
      if (col.dbField?.includes('type')) {
        return {
          ...col,
          headerName: t('Type'),
          width: 150,
          valueFormatter: (value: string): string => {
            switch (value) {
              case 'session':
                return t('Séance')
              case 'sub_event':
                return t('Intervention')
              default:
                return String(value)
            }
          },
        }
      }
      return col
    }) as GridColDef[]
  }, [data?.columns, event?.name, rolesOptions, participantOptionStatusOptions, t])

  return (
    <Stack
      sx={theme => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: '0.5rem',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: theme.boxShadows.main,
      })}
      height="inherit"
      overflow="hidden auto"
      padding="0.5rem"
    >
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h2">{t('Détails événement')}</Typography>
        <Typography variant="h2">{event?.name}</Typography>
      </Stack>

      {showStatistics && <AdminEventDetails event={event} />}

      <Divider />
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ margin: '1rem 0' }}
        >
          <h3>{t('Liste des contributeurs')} :</h3>
          <ViewButtonGroup />
        </Stack>
        <Stack
          sx={{
            maxHeight: `calc(100vh - ${HEADER_HEIGHT} - ${SUB_HEADER_HEIGHT} - 40px - 4.5rem${!showStatistics ? ' - 60px' : ''})`,
            overflow: 'auto',
          }}
        >
          <StripedDataGrid
            rows={data?.rows}
            columns={columns}
            columnGroupingModel={data?.column_groups}
            count={data?.rows.length}
            paginationMode="client"
            initialState={initialState}
            emailOptions={{
              getValue: row => (row as Participants<true>).e_mail,
              body: `Bonjour,%0D%0A%0D%0ANous venons de mettre à jour votre participation en tant que contributeur pour l'événement ${event?.name}.%0D%0A%0D%0ACordialement,%0D%0AL'équipe SFCTCV`,
              subject: `SFCTCV Événement ${event?.name} - Information sur votre contribution`,
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}
